import React from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '../../contexts/AuthContext';
import { useHousehold } from '../../hooks/useHousehold';
import { itemsApi } from '../../api/items';
import { L } from '../../lib/labels';

function HomeIcon() {
  return (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function CartNavIcon({ needsCount }: { needsCount: number }) {
  return (
    <div className="relative">
      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
      {needsCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 bg-amber-400 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
          {needsCount > 99 ? '99+' : needsCount}
        </span>
      )}
    </div>
  );
}

function ClockIcon() {
  return (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CrownIcon() {
  return (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l3.5 5L12 3l3.5 5L19 3v12H5V3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15h14v2H5z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  [
    'flex-1 flex flex-col items-center justify-center gap-0.5',
    'touch-manipulation transition-colors',
    isActive ? 'text-brand-600' : 'text-gray-400 hover:text-gray-600',
  ].join(' ');

const menuBtnClass = 'flex-1 flex flex-col items-center justify-center gap-0.5 touch-manipulation transition-colors text-gray-400 hover:text-gray-600 active:text-brand-600';

interface Props {
  onOpenMenu: () => void;
}

export function BottomNav({ onOpenMenu }: Props) {
  const { isAdmin } = useAuthContext();
  const { data: household } = useHousehold();

  const isPremium = isAdmin || ['PREMIUM', 'COURTESY', 'LIFE_PARTNER'].includes(household?.plan ?? '');
  const isFreeUser = !isAdmin && !isPremium;

  const { data: needsItems } = useQuery({
    queryKey: ['items', { needsToBuy: true }],
    queryFn: () => itemsApi.list({ needsToBuy: true }),
    staleTime: 30_000,
  });

  const needsCount = needsItems?.length ?? 0;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none">
      <div
        className="mx-auto w-full max-w-md bg-white border-t border-gray-200 pointer-events-auto"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex h-16">
          <NavLink to="/items" className={navLinkClass}>
            <HomeIcon />
            <span className="text-xs font-medium">{L.nav.items}</span>
          </NavLink>

          <NavLink to="/shopping" className={navLinkClass}>
            <CartNavIcon needsCount={needsCount} />
            <span className="text-xs font-medium">{L.nav.shopping}</span>
          </NavLink>

          <NavLink to="/history" className={navLinkClass}>
            <ClockIcon />
            <span className="text-xs font-medium">{L.nav.history}</span>
          </NavLink>

          {isFreeUser && (
            <NavLink to="/settings/payment" className={navLinkClass}>
              <CrownIcon />
              <span className="text-xs font-medium">{L.nav.premium}</span>
            </NavLink>
          )}

          {isAdmin && (
            <NavLink to="/admin" className={navLinkClass}>
              <ShieldIcon />
              <span className="text-xs font-medium">{L.nav.admin}</span>
            </NavLink>
          )}

          <button onClick={onOpenMenu} className={menuBtnClass}>
            <MenuIcon />
            <span className="text-xs font-medium">{L.nav.menu}</span>
          </button>
        </div>
      </div>
    </nav>
  );
}
